// trendTicks — the ONE x-axis tick computation for every filter-driven trend chart
// (the balance line/area, the stacked composition view, the passbook bars).
//
// Rule ("X-axis labels — filters, divisions, and format"): EQUAL DIVISIONS. The plotted
// span is cut into N equal intervals and a tick goes on every boundary, so the first tick
// is the real first date, the last is today, and EVERY gap on the axis is the same width.
// The old rule stepped a fixed size forward from the start and stopped at today, which
// left the final gap a partial step — 9 days against 7 on 1M, 3.4 years against 5 on All —
// and that is the uneven spacing this replaces. Never more than 6 labels on any filter.
//
//   1M · 3M · 1Y → 4 divisions (5 labels) · 5Y · All → 5 divisions (6 labels)
//
// The division count is chosen so each filter's interval lands on its natural unit: 1M/4 ≈
// a week, 3M/4 ≈ three weeks, 1Y/4 = a quarter, 5Y/5 = a year. A filter clamped short by
// enrollment keeps 4 divisions, so a four-month "1Y" reads monthly instead of leaving start
// and today alone. `division` names the unit the interval landed on, for callers that
// report it.
//
// Labels: an interval shorter than a month (1M's ~7.5 days, 3M's ~22.5) prints the full
// date as "DD MMM,YY" ("18 Aug,26") on every tick, since several ticks fall inside one
// month. A month or longer prints the month name, and adds the year at the two ends or
// whenever it differs from the label before it ("Jun '25", "Sep", "Dec", "Mar '26") —
// unless that would print the same month twice in a row, which sends it back to full
// dates.
//
// A tick's DATE is rounded to the nearest day for its label, but its POSITION is the exact
// boundary — `fractions` is where it sits along the time axis, so the even spacing survives
// a half-day rounding. `indices` (nearest plotted point) serves the banded bar view, whose
// columns have no continuous axis.
//
// Pure, UTC date math (a bare "2026-06-03" must never slip a day across timezones).

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAY_MS = 24 * 3600e3;

/**
 * Divisions per filter. Five for the two long filters (6 labels, the most the axis takes),
 * four everywhere else — including a fixed filter that enrollment clamped short, which
 * simply divides the shorter span into the same four parts.
 */
const DIVISIONS = { '1M': 4, '3M': 4, '1Y': 4, '5Y': 5 };
const DEFAULT_DIVISIONS = 5;

// The unit an interval of `days` reads as. Reported as `division`; also decides whether the
// labels carry a day (a week-or-three interval puts several ticks inside one month).
const UNITS = [
  [10, 'week'], [24, '3week'], [45, 'month'], [100, 'quarter'], [200, 'half'],
  [400, 'year'], [800, '2y'], [2000, '5y'], [Infinity, '10y'],
];

const toUTCDate = (d) => {
  if (d instanceof Date) return d;
  const s = String(d);
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(s) ? `${s}T00:00:00Z` : s);
};

/** The unit key for an interval of `days` — 'week' … '10y'. */
export function unitForDays(days) {
  return UNITS.find(([limit]) => days <= limit)[1];
}

/**
 * How many equal divisions a filter's span gets. Never more than the span has whole days
 * (a three-day window must not ask for four ticks) and never more than the plotted points
 * can distinguish, so a sparse series doesn't label the same point twice.
 */
export function divisionsFor(period, spanDays, pointCount = Infinity) {
  const wanted = DIVISIONS[period] ?? DEFAULT_DIVISIONS;
  return Math.max(1, Math.min(wanted, Math.floor(spanDays) || 1, pointCount - 1));
}

/** `n + 1` boundary dates cutting [start, end] into n equal intervals. */
export function divisionDates(start, end, n) {
  const span = end - start;
  if (!(span > 0) || n < 1) return [start];
  return Array.from({ length: n + 1 }, (_, k) => new Date(start.getTime() + (span * k) / n));
}

/** The day a tick's label names: the boundary rounded to the nearest UTC midnight. */
const labelDay = (d) => new Date(Math.round(d.getTime() / DAY_MS) * DAY_MS);

function formatLabel(d, { isEnd, prevYear, showDay }) {
  const y = d.getUTCFullYear();
  const yy = String(y).slice(-2);
  const month = MONTHS[d.getUTCMonth()];
  // "04 Mar,26" — day first, the month spelled, the year two digits. The hover card
  // answers in the same shape (utils/format `formatDayMonthYear`), so a point's label and
  // the card above it can't read as two different conventions.
  if (showDay) return `${String(d.getUTCDate()).padStart(2, '0')} ${month},${yy}`;
  const year = isEnd || prevYear !== y ? ` '${yy}` : '';
  return `${month}${year}`;
}

// Index of the plotted point nearest to time `t` (points are sorted ascending).
function nearestIndex(ts, t) {
  let lo = 0;
  let hi = ts.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ts[mid] < t) lo = mid + 1; else hi = mid;
  }
  return lo > 0 && t - ts[lo - 1] < ts[lo] - t ? lo - 1 : lo;
}

/**
 * X-axis ticks for one period's plotted dates.
 * @param {{ period: string, dates: Array<string|Date>, divisions?: number }} args — the
 *   filter, the per-point dates (1:1 with the series), and an optional division override
 *   for a caller with less width to label into (a phone).
 * @returns {{ dates: Date[], labels: string[], fractions: number[], indices: number[], division: string|null }}
 *   `fractions` — where each tick sits along the time axis (0 = first point, 1 = last);
 *   `indices` — the nearest plotted point, for banded (bar) axes.
 */
export function trendTicks({ period, dates, divisions }) {
  if (!Array.isArray(dates) || dates.length === 0) {
    return { dates: [], labels: [], fractions: [], indices: [], division: null };
  }
  const ds = dates.map(toUTCDate);
  const n = ds.length;
  const start = ds[0];
  const end = ds[n - 1];
  const spanMs = end - start;
  const spanDays = spanMs / DAY_MS;
  const parts = Math.max(1, Math.min(
    divisions || Infinity,
    divisionsFor(period, spanDays, n),
  ));
  const bounds = divisionDates(start, end, parts);
  // The unit the interval landed on — 'week' for 1M's ~7.5 days, 'quarter' for 1Y's ~91.
  const division = spanDays > 0 ? unitForDays(spanDays / parts) : null;
  // Several ticks inside one month need the day to tell them apart; a month or more per
  // interval does not, and the bare month reads better.
  const showDay = spanDays > 0 && spanDays / parts < 28;
  const ts = ds.map((d) => d.getTime());

  const days = bounds.map(labelDay);
  const label = (showDayParts) => days.map((d, k) => formatLabel(d, {
    isEnd: k === 0 || k === days.length - 1,
    prevYear: k > 0 ? days[k - 1].getUTCFullYear() : null,
    showDay: showDayParts,
  }));
  // Month labels are cleaner where they are unambiguous, but a ~30-day interval can still
  // land twice inside one month ("Mar", "Mar"). Two ticks reading the same is worse than
  // the longer form, so that sends the whole axis back to full dates.
  const monthLabels = showDay ? null : label(false);
  const labels = monthLabels && !monthLabels.some((l, k) => k > 0 && l === monthLabels[k - 1])
    ? monthLabels
    : label(true);
  // Positions come from the EXACT boundary, not the rounded label day, so the gaps stay
  // equal — k/parts by construction.
  const fractions = bounds.map((d) => (spanMs > 0 ? (d - start) / spanMs : 0));
  const indices = bounds.map((d, k) => (
    k === 0 ? 0 : k === bounds.length - 1 ? n - 1 : nearestIndex(ts, d.getTime())
  ));
  return { dates: days, labels, fractions, indices, division };
}

/**
 * Each point's x position as a fraction of the axis (0 = first date, 1 = last), proportional
 * to TIME rather than index. The backend's calendar-bucketed points have partial edge
 * buckets (enrollment → first boundary, last boundary → today), so spacing them evenly by
 * index stretches or squeezes the end segments and the tick gaps with them. Without usable
 * dates (or a zero span) it falls back to even index spacing.
 * @param {Array<string|Date>|undefined} dates
 * @param {number} count — the series length the fractions must match
 * @returns {number[]}
 */
export function timeFractions(dates, count) {
  const even = Array.from({ length: count }, (_, i) => (count > 1 ? i / (count - 1) : 0));
  if (!Array.isArray(dates) || dates.length !== count || count < 2) return even;
  const ts = dates.map((d) => toUTCDate(d).getTime());
  if (ts.some((t) => Number.isNaN(t))) return even;
  const t0 = ts[0];
  const span = ts[count - 1] - t0;
  if (span <= 0) return even;
  return ts.map((t) => Math.min(1, Math.max(0, (t - t0) / span)));
}

export default trendTicks;
